'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { User, AuthError, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { useRouter } from 'next/navigation'

// Create auth context type
type AuthState = {
  user: User | null
  session: Session | null
  error: AuthError | null
  isLoading: boolean
  signUp: (email: string, password: string, fullName: string, companyName: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  getUserTier: (userId?: string) => Promise<string>
}

// Create context with default values
const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  error: null,
  isLoading: true,
  signUp: async () => {},
  signIn: async () => {},
  signOut: async () => {},
  signInWithGoogle: async () => {},
  getUserTier: async () => 'free'
})

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext)

export function SupabaseAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [error, setError] = useState<AuthError | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  // Initialize session and set up auth state listener
  useEffect(() => {
    setIsLoading(true)

    // Get initial session
    const initializeAuth = async () => {
      try {
        // Check for existing session
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          console.error('Auth Provider - Error fetching session:', sessionError)
          throw sessionError
        }
        
        if (initialSession) {
          setSession(initialSession)
          setUser(initialSession.user)
          console.log('Auth Provider - Initialized with user:', initialSession.user.email)
        }
      } catch (error) {
        console.error('Auth Provider - Error during initialization:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth Provider - Auth state changed:', event)
        
        if (currentSession) {
          // Always update session (needed for fresh access tokens)
          setSession(currentSession)
          // Only update user if the user ID actually changed to avoid
          // triggering useEffect cascades on token refreshes (e.g. tab focus)
          setUser(prev => {
            if (prev?.id === currentSession.user.id) return prev
            return currentSession.user
          })
        } else {
          setSession(null)
          setUser(null)
          console.log('Auth Provider - No session')
        }
        
        setIsLoading(false)
      }
    )

    // Clean up subscription on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Get user subscription tier
  const getUserTier = async (userId?: string) => {
    try {
      const targetId = userId || user?.id
      if (!targetId) return 'free'
      
      const { data, error } = await supabase
        .from('subscriptions')
        .select('tier, status')
        .eq('user_id', targetId)
        .single()
      
      if (error) {
        console.error('Auth Provider - Error fetching subscription:', error)
        return 'free'
      }
      
      return (data?.status === 'active') ? data.tier : 'free'
    } catch (error) {
      console.error('Auth Provider - Error in getUserTier:', error)
      return 'free'
    }
  }

  // Sign up new user
  const signUp = async (email: string, password: string, fullName: string, companyName: string) => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Sign up with email and password
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            company_name: companyName
          }
        }
      })
      
      if (error) throw error
      
      // If email confirmation is required, user won't be signed in yet
      if (data.user && !data.session) {
        console.log('Auth Provider - User created, email confirmation required')
        return
      }
      
      // If email confirmation is not required, user is signed in
      // Navigation is handled by the calling page (e.g. signup page starts trial first)
      if (data.session) {
        setUser(data.user)
        setSession(data.session)
      }
    } catch (error) {
      console.error('Auth Provider - Sign up error:', error)
      setError(error as AuthError)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Sign in with email/password
  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      setError(null)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      // Set session first
      setSession(data.session)
      setUser(data.user)
      
      // Wait for a tick to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 0))
      
      // Only navigate if we still have a session
      if (data.session) {
        // Use replace instead of push to prevent back button issues
        router.replace('/dashboard')
      }
    } catch (error) {
      console.error('Auth Provider - Sign in error:', error)
      setError(error as AuthError)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      setIsLoading(true)
      
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      setUser(null)
      setSession(null)
      
      // Redirect to home page
      router.push('/')
    } catch (error) {
      console.error('Auth Provider - Sign out error:', error)
      setError(error as AuthError)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // No explicit redirectTo here. Supabase will use the Site URL + /auth/callback
      // from your Supabase project settings.
      // Ensure your Supabase Dashboard's "Site URL" is correctly set for both dev
      // (e.g., http://localhost:3000) and prod (e.g., https://www.govcon-data.com).
      // Also ensure Google Cloud Console "Authorized redirect URIs" includes
      // [Supabase Site URL]/auth/callback for each environment.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
      });
      
      if (error) {
        console.error('Auth Provider - Google sign-in error:', error);
        setError(error as AuthError); // Cast error to AuthError
        throw error;
      }
      
      // Auth state will be handled by the onAuthStateChange listener
      // User will be redirected to Google, then back to /auth/callback,
      // and the onAuthStateChange listener will pick up the session.
      // No explicit router.push() needed here as the page will reload
      // after the OAuth redirect dance.

    } catch (err) {
      let typedError: AuthError;
      if (err instanceof AuthError) {
        typedError = err;
      } else {
        const message = (err instanceof Error) ? err.message : 'An unknown error occurred during Google sign-in.';
        // Create a new AuthError with just the message.
        // Log the original error separately if more detail is needed.
        typedError = new AuthError(message);
        if (!(err instanceof AuthError)) { // Avoid double logging if it was already an AuthError
            console.error('Original error during Google sign-in:', err);
        }
      }
      
      console.error('Auth Provider - Google sign-in AuthError:', typedError.message);
      setError(typedError);
      // Optionally, re-throw if you want calling components to also handle it
      // throw typedError; 
    } finally {
      setIsLoading(false)
    }
  }

  const value = {
    user,
    session,
    error,
    isLoading,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    getUserTier
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
} 